import { useState, useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Image,
  Linking,
} from "react-native";
import {
  Shield,
  Building2,
  Hash,
  Heart,
  CheckCircle2,
  Pencil,
  FileText,
  Plus,
  RotateCcw,
} from "lucide-react-native";
import * as DocumentPicker from "expo-document-picker";
import type {
  InsurancePolicy,
  FertilityCoverage as FertilityCoverageType,
  VerificationStatus,
} from "../../../../../../packages/coverage/hooks/useCoverage";
import { useSaveCoverage } from "../../../../../../packages/coverage/hooks/useCoverage";
import { usePayersSearch } from "@src/packages/coverages/hooks/usePayers";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  useDeleteDocument,
  useDocuments,
  useGetDownloadUrl,
} from "@src/packages/documents/hooks/useDocuments";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
} from "@src/packages/documents/ingestion";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import {
  CardSubtitleText,
  CardSurface,
  CardTitleText,
  Dialog,
  DialogIcon,
  DialogBodyText,
  DialogHeader,
  DialogTitleText,
  DialogActions,
  FieldMessage,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  IconButton,
  MoonDetailRow,
  MoonFormField,
  StatusBadge,
  SupportingSubtitleText,
  TextActionButton,
  compactSecondarySurfaceStyle,
  selectMenuItemStyle,
  selectMenuSurfaceStyle,
  semanticColors,
  TextField,
  LoadingState,
  ActionBanner,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import { EMBER } from "../../../../onboarding/theme";

const umbrellaIcon =
  require("../../../../assets/images/you-icons/umbrella.png") as number;

type Props = {
  visible: boolean;
  onClose: () => void;
  primaryPolicy: InsurancePolicy | undefined;
  hasInsurance: boolean;
  verificationStatus: VerificationStatus;
  fertilityCoverage: FertilityCoverageType | null;
  isLoading: boolean;
  /**
   * Most existing callers open the prefilled editor. Coverage & Benefits uses
   * the summary by default, with an explicit edit action from the card.
   */
  initialMode?: "summary" | "edit";
};

type CardSideName = "front" | "back";

/* ── Insurance Form (add or edit) ── */

function InsuranceForm({
  onSuccess,
  onCancel,
  initialValues,
  isEdit = false,
}: {
  onSuccess: () => void;
  onCancel: () => void;
  initialValues?: {
    carrier: string;
    payerId?: string | null;
    memberId: string;
    groupNumber: string;
    policyId?: string;
  };
  isEdit?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("InsuranceForm");
  const [carrier, setCarrier] = useState(initialValues?.carrier ?? "");
  // Keep the canonical payer identifier alongside the display name. The
  // verification workflow relies on this ID, not just the free-text carrier.
  const [payerId, setPayerId] = useState<string | null>(
    initialValues?.payerId ?? null,
  );
  const [memberId, setMemberId] = useState(initialValues?.memberId ?? "");
  const [groupNumber, setGroupNumber] = useState(
    initialValues?.groupNumber ?? "",
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [formError, setFormError] = useState(false);
  // The policy ID is seeded when editing and captured after an explicit Save,
  // so a later Save updates the same policy instead of duplicating it.
  const [persistedPolicyId, setPersistedPolicyId] = useState<
    string | undefined
  >(initialValues?.policyId);

  // Each card side is filed as its own Document because the API does not
  // support appending uploads to an existing document.
  type CardSide = {
    uploadId: string;
    name: string;
    contentType: string;
    /** Local picker URI — only valid until the user closes the modal. */
    localUri: string | null;
    /** Server-side presigned URL — used when restoring from a saved doc. */
    remoteUrl: string | null;
  };
  // Each side lives in its own Document tagged side:front / side:back so
  // re-uploading one side never touches the other.
  const [frontDocId, setFrontDocId] = useState<string | null>(null);
  const [backDocId, setBackDocId] = useState<string | null>(null);
  const [frontSide, setFrontSide] = useState<CardSide | null>(null);
  const [backSide, setBackSide] = useState<CardSide | null>(null);
  const [processingSides, setProcessingSides] = useState<Set<CardSideName>>(
    () => new Set(),
  );
  const [cardReadingSide, setCardReadingSide] = useState<CardSideName | null>(
    null,
  );
  const [cardUploadError, setCardUploadError] = useState<CardSideName | null>(
    null,
  );
  const [cardOcrError, setCardOcrError] = useState<CardSideName | null>(null);
  const [cardPrefillDone, setCardPrefillDone] = useState(false);
  // Which side the user is confirming deletion for (drives the confirm
  // dialog); null when no confirmation is open.
  // The side whose "Edit" was tapped: its dialog offers a new upload or removal.
  const [manageSide, setManageSide] = useState<CardSideName | null>(null);

  // Card documents are staged during the form session. Newly created docs are
  // deleted on Cancel/close; replaced or removed original docs are deleted
  // only after the policy Save succeeds.
  const cardDocsToDeleteOnSaveRef = useRef(new Set<string>());
  const transactionActiveRef = useRef(true);

  const { tenantId, patientId } = useApiClient();
  const deleteDocument = useDeleteDocument();
  const getDownloadUrl = useGetDownloadUrl();
  const transfer = useDocumentSourceTransfer();
  const { data: allDocuments } = useDocuments();

  const deleteDocuments = useCallback(
    async (docIds: Iterable<string>) => {
      for (const docId of new Set(docIds)) {
        try {
          await deleteDocument.mutateAsync(docId);
        } catch (error) {
          console.warn("Failed to clean up insurance card document:", error);
        }
      }
    },
    [deleteDocument],
  );

  useEffect(() => {
    return () => {
      transactionActiveRef.current = false;
    };
  }, []);

  // Prefill the front/back rows from existing per-side documents on the
  // server. Each side lives in its own document tagged side:front /
  // side:back. Backward-compat: a legacy doc tagged only "insurance-card"
  // is treated as the front. Guarded by cardPrefillDone so this only runs
  // once per form mount.
  useEffect(() => {
    if (cardPrefillDone) return;
    if (!allDocuments) return;
    setCardPrefillDone(true);

    const insuranceDocs = allDocuments
      .filter((d) => d.tags.includes("insurance-card"))
      .slice()
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));

    const frontDoc =
      insuranceDocs.find((d) => d.tags.includes("side:front")) ??
      insuranceDocs.find(
        (d) => !d.tags.includes("side:front") && !d.tags.includes("side:back"),
      );
    const backDoc = insuranceDocs.find((d) => d.tags.includes("side:back"));

    const buildSide = (u: {
      id: string;
      name: string | null;
      contentType: string | null;
    }): CardSide => {
      const contentType =
        u.contentType ??
        (u.name?.toLowerCase().endsWith(".pdf")
          ? "application/pdf"
          : "image/jpeg");
      return {
        uploadId: u.id,
        name: u.name ?? "Insurance Card",
        contentType,
        localUri: null,
        remoteUrl: null,
      };
    };

    const extractSide = (doc: (typeof insuranceDocs)[number] | undefined) => {
      if (!doc) return { docId: null, side: null };
      const upload = doc.uploads.find((u) => u.uploadStatus === "uploaded");
      if (!upload) return { docId: doc.id, side: null };
      return { docId: doc.id, side: buildSide(upload) };
    };

    const { docId: frontId, side: initialFront } = extractSide(frontDoc);
    const { docId: backId, side: initialBack } = extractSide(backDoc);
    if (frontId) setFrontDocId(frontId);
    if (backId) setBackDocId(backId);
    if (initialFront) setFrontSide(initialFront);
    if (initialBack) setBackSide(initialBack);

    // Fetch presigned URLs so images render in the row preview.
    const hydrate = async (side: CardSide) => {
      if (!side.contentType.startsWith("image/")) return side;
      try {
        const url = await getDownloadUrl.mutateAsync(side.uploadId);
        if (typeof url === "string") return { ...side, remoteUrl: url };
      } catch (err) {
        console.warn("Failed to fetch card download URL:", err);
      }
      return side;
    };
    if (initialFront) hydrate(initialFront).then((s) => setFrontSide(s));
    if (initialBack) hydrate(initialBack).then((s) => setBackSide(s));
  }, [allDocuments, cardPrefillDone, getDownloadUrl]);

  const {
    data: payerSuggestions,
    isFetching: isSearchingPayers,
    isError: payersSearchFailed,
  } = usePayersSearch(carrier);

  const saveCoverage = useSaveCoverage();

  // Persist the policy to the coverage API. Used by the explicit Save button
  // policy instead of creating a duplicate.
  const persistCoverage = async (values: {
    carrier: string;
    memberId: string;
    groupNumber: string;
  }): Promise<boolean> => {
    try {
      const result = await saveCoverage.mutateAsync({
        userInsurancePolicies: [
          {
            ...(persistedPolicyId ? { id: persistedPolicyId } : {}),
            payerId,
            insuranceCarrier: values.carrier.trim(),
            memberId: values.memberId.trim(),
            ...(values.groupNumber.trim()
              ? { groupNumber: values.groupNumber.trim() }
              : {}),
          },
        ],
      });
      // PATCH can return more than one policy. Identify the policy just
      // saved rather than assuming index zero, so editing a second policy
      // continues to target that same policy on a subsequent save.
      const savedPolicy = result?.userInsurancePolicies?.find(
        (policy) =>
          (persistedPolicyId !== undefined &&
            policy.id === persistedPolicyId) ||
          policy.memberId === values.memberId.trim(),
      );
      if (savedPolicy?.id) setPersistedPolicyId(savedPolicy.id);
      if (savedPolicy) setPayerId(savedPolicy.payerId ?? null);
      return true;
    } catch {
      // saveCoverage.onError logs; saveCoverage.isError drives the inline UI.
      return false;
    }
  };

  // Upload one side of the insurance card. The local preview is shown
  // immediately on pick so the user sees the slot fill before any network
  // round-trip. Document replacement/deletion is staged until Save, and OCR
  // only fills the draft. OCR is wrapped in a timeout so a hung /extract call
  // cannot leave the tile stuck.
  const handlePickCard = async (side: CardSideName) => {
    if (!tenantId || !patientId) return;
    if (processingSides.has(side) || processingSides.size > 0) return;

    const result = await DocumentPicker.getDocumentAsync({
      type: ["application/pdf", "image/*"],
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets?.[0]) return;
    const asset = result.assets[0];
    const previousSide = side === "front" ? frontSide : backSide;
    const previousDocId = side === "front" ? frontDocId : backDocId;

    const contentType = resolveUploadContentType({
      name: asset.name,
      uri: asset.uri,
      mimeType: asset.mimeType,
    });
    const isImage = contentType.startsWith("image/");
    const fileExt = isImage ? "jpg" : "pdf";
    const uploadName =
      asset.name || `insurance-card-${side}-${Date.now()}.${fileExt}`;

    // Show the preview right away — even before uploadId exists. The
    // uploadId gets filled in once S3 + doc creation finish.
    const optimisticSide: CardSide = {
      uploadId: "", // placeholder until upload completes
      name: uploadName,
      contentType,
      localUri: isImage ? asset.uri : null,
      remoteUrl: null,
    };
    if (side === "front") setFrontSide(optimisticSide);
    else setBackSide(optimisticSide);
    setFormError(false);
    setCardUploadError(null);
    setCardOcrError(null);

    setProcessingSides((current) => new Set(current).add(side));
    try {
      const source = await transfer.uploadSource(
        {
          uri: asset.uri,
          name: uploadName,
          mimeType: contentType,
        },
        {
          destination: "insurance",
          sourceContext: {
            surface: "insurance_info_modal",
            route: "InsuranceInfoModal",
            form: "insurance",
            targetHints: [side],
            allowBackgroundProcessing: true,
          },
        },
      );
      const uploadId = source.uploadId;

      // Create a replacement source without deleting the old one. The old
      // document is only removed after the policy Save succeeds. Processing
      // runs for both card sides; only the front projects fields into this
      // form.
      const outcome = await transfer.processSource(source, {
        attempts: 24,
        intervalMs: 1_250,
      });
      const newDocId = outcome.patientDocumentId;
      if (!transactionActiveRef.current) return;

      const settledSide: CardSide = { ...optimisticSide, uploadId };
      if (previousDocId) {
        cardDocsToDeleteOnSaveRef.current.add(previousDocId);
      }
      if (side === "front") {
        setFrontSide(settledSide);
        setFrontDocId(newDocId);
      } else {
        setBackSide(settledSide);
        setBackDocId(newDocId);
      }

      // Contextual prefill is front-only. The canonical run still retains all
      // claims, segments, parties, completeness, and evidence for either side.
      if (side === "front") {
        setCardReadingSide(side);
        try {
          const projected = projectDocumentProcessingRunToPrefill(
            outcome.run,
            uploadName,
          );
          const insurance = projected.insurance;
          if (
            insurance.payerName ||
            insurance.memberId ||
            insurance.groupNumber
          ) {
            setFormError(false);
            // Only fill fields the user hasn't already typed into.
            const nextCarrier =
              insurance.payerName && !carrier.trim()
                ? insurance.payerName
                : carrier;
            const nextMemberId =
              insurance.memberId && !memberId.trim()
                ? insurance.memberId
                : memberId;
            const nextGroupNumber =
              insurance.groupNumber && !groupNumber.trim()
                ? insurance.groupNumber
                : groupNumber;
            if (nextCarrier !== carrier) {
              setCarrier(nextCarrier);
              setPayerId(null);
            }
            if (nextMemberId !== memberId) setMemberId(nextMemberId);
            if (nextGroupNumber !== groupNumber) {
              setGroupNumber(nextGroupNumber);
            }
            setCardOcrError(null);
          }
        } catch (processingError) {
          console.warn(
            "Insurance card canonical prefill failed:",
            processingError,
          );
          if (transactionActiveRef.current) setCardOcrError(side);
        } finally {
          if (transactionActiveRef.current) setCardReadingSide(null);
        }
      }
    } catch (error) {
      console.error("Insurance card upload failed:", error);
      // Roll back to the previous staged/original card so a failed replace
      // never makes a previously saved image disappear.
      if (transactionActiveRef.current) {
        if (side === "front") {
          setFrontSide(previousSide);
          setFrontDocId(previousDocId);
        } else {
          setBackSide(previousSide);
          setBackDocId(previousDocId);
        }
        setCardUploadError(side);
      }
    } finally {
      if (transactionActiveRef.current) {
        setProcessingSides((current) => {
          const next = new Set(current);
          next.delete(side);
          return next;
        });
        if (cardReadingSide === side) setCardReadingSide(null);
      }
    }
  };

  // Open the actual file (used for PDFs). Fetches a presigned download URL
  // and opens it in a new tab on web / system viewer on native.
  const openUploadedFile = async (uploadId: string) => {
    try {
      const url = await getDownloadUrl.mutateAsync({
        uploadId,
        disposition: "attachment",
      });
      if (typeof url === "string") {
        Linking.openURL(url);
      }
    } catch (error) {
      console.error("Failed to open uploaded file:", error);
    }
  };

  // Stage one side of the insurance card for deletion. The backing document
  // stays intact until Save succeeds, so Cancel can restore the original card.
  const handleDeleteCard = (side: CardSideName) => {
    const docId = side === "front" ? frontDocId : backDocId;
    if (docId) {
      cardDocsToDeleteOnSaveRef.current.add(docId);
    }
    if (side === "front") {
      setFrontSide(null);
      setFrontDocId(null);
    } else {
      setBackSide(null);
      setBackDocId(null);
    }
    setCardUploadError(null);
    setCardOcrError(null);
    setManageSide(null);
  };

  const handleSubmit = async () => {
    if (saveCoverage.isPending || processingSides.size > 0) return;
    // An earlier save failure must not sit beside a new validation message.
    saveCoverage.reset();
    if (
      !carrier.trim() &&
      !memberId.trim() &&
      !groupNumber.trim() &&
      !frontSide &&
      !backSide
    ) {
      setFormError(true);
      return;
    }
    setFormError(false);

    const saved = await persistCoverage({ carrier, memberId, groupNumber });
    if (!saved || !transactionActiveRef.current) return;

    const currentDocIds = [frontDocId, backDocId].filter(
      (docId): docId is string => docId !== null,
    );
    await deleteDocuments(
      [...cardDocsToDeleteOnSaveRef.current].filter(
        (docId) => !currentDocIds.includes(docId),
      ),
    );
    cardDocsToDeleteOnSaveRef.current.clear();
    onSuccess();
  };

  return (
    <View style={{ gap: 14 }}>
      <DialogBodyText
        style={{ textAlign: "center", marginTop: 0, marginBottom: 2 }}
      >
        {isEdit
          ? t("you.insurance_modal.edit_blurb")
          : t("you.insurance_modal.add_blurb")}
      </DialogBodyText>

      {/* Insurance card upload: two rows, front and back. Tapping a filled row
          opens the card; its one Edit button leads to uploading a new image or
          removing this one, so a removal is never a stray tap. */}
      <MoonFormField label={t("you.insurance_modal.insurance_card_label")}>
        <CardSurface style={{ overflow: "hidden", paddingVertical: 4 }}>
          {(["front", "back"] as const).map((side, idx) => {
            const sideState = side === "front" ? frontSide : backSide;
            const isProcessing = processingSides.has(side);
            const isImage =
              sideState?.contentType.startsWith("image/") ?? false;
            const photoUri = sideState
              ? (sideState.localUri ?? sideState.remoteUrl)
              : null;
            const showPhoto = sideState && isImage && photoUri;
            const isFilled = !!sideState;
            const label = t(
              side === "front"
                ? "you.insurance_modal.front_card_label"
                : "you.insurance_modal.back_card_label",
            );
            const sideName = label.toLowerCase();
            const hasUploadError = cardUploadError === side;
            const canView = isFilled && !!sideState?.uploadId;

            return (
              <Pressable
                key={side}
                onPress={() => {
                  if (isProcessing) return;
                  capturePress(pressLabel(label), "insurance_card_side_row");
                  // Empty row opens picker; filled row opens viewer.
                  // Replace and remove live behind the Edit button.
                  if (!isFilled || hasUploadError) {
                    handlePickCard(side);
                  } else if (canView) {
                    openUploadedFile(sideState!.uploadId);
                  }
                }}
                disabled={isProcessing}
                accessibilityRole="button"
                accessibilityLabel={t(
                  isFilled
                    ? "you.insurance_modal.open_card_action"
                    : hasUploadError
                      ? "you.insurance_modal.retry_card_action"
                      : "you.insurance_modal.add_card_action",
                  { params: { side: sideName } },
                )}
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 14,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 12,
                  backgroundColor: "transparent",
                }}
              >
                {/* Thumbnail — photo, file icon, or empty + */}
                <View
                  style={{
                    width: 54,
                    height: 36,
                    borderRadius: 8,
                    overflow: "hidden",
                    alignItems: "center",
                    justifyContent: "center",
                    ...(isFilled
                      ? {
                          backgroundColor:
                            semanticColors.action.tonal.background,
                        }
                      : compactSecondarySurfaceStyle),
                  }}
                >
                  {showPhoto ? (
                    <Image
                      source={{ uri: photoUri! }}
                      style={{ width: "100%", height: "100%" }}
                      resizeMode="cover"
                    />
                  ) : isFilled ? (
                    <FileText
                      size={18}
                      color={semanticColors.action.tonal.foreground}
                      strokeWidth={1.6}
                    />
                  ) : (
                    <Plus
                      size={16}
                      color={semanticColors.text.tertiary}
                      strokeWidth={2.2}
                    />
                  )}
                </View>

                {/* Label + meta */}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <CardTitleText>{label}</CardTitleText>
                  <CardSubtitleText
                    numberOfLines={1}
                    style={
                      hasUploadError ? { color: EMBER.colors.coral } : null
                    }
                  >
                    {isProcessing
                      ? cardReadingSide === side
                        ? t("you.insurance_modal.card_reading")
                        : t("you.insurance_modal.card_uploading")
                      : hasUploadError
                        ? t("you.insurance_modal.card_upload_error")
                        : isFilled
                          ? sideState!.name
                          : t("you.insurance_modal.card_add_hint")}
                  </CardSubtitleText>
                  {cardOcrError === side && (
                    <FieldMessage style={{ marginTop: 2 }}>
                      {t("you.insurance_modal.card_ocr_error")}
                    </FieldMessage>
                  )}
                </View>

                {/* Right-side actions */}
                {isProcessing ? (
                  <ThinkingOrbStatus
                    state={cardReadingSide === side ? "shaping" : "composing"}
                    label={
                      cardReadingSide === side
                        ? t("you.insurance_modal.card_reading")
                        : t("you.insurance_modal.card_uploading")
                    }
                    size={20}
                  />
                ) : isFilled ? (
                  <IconButton
                    onPress={(e) => {
                      e.stopPropagation();
                      setManageSide(side);
                    }}
                    hitSlop={6}
                    accessibilityLabel={t(
                      "you.insurance_modal.edit_card_side",
                      {
                        params: { side: sideName },
                      },
                    )}
                    action="you.insurance_modal.edit_card_side"
                    size={32}
                    surface="soft"
                  >
                    <Pencil
                      size={14}
                      color={semanticColors.action.tonal.foreground}
                      strokeWidth={1.8}
                    />
                  </IconButton>
                ) : (
                  // The whole row is the button; this pill only shows the
                  // action, in the compact tonal colours.
                  <View
                    style={{
                      minHeight: 34,
                      justifyContent: "center",
                      paddingHorizontal: 11,
                      borderRadius: 999,
                      backgroundColor: semanticColors.action.tonal.background,
                    }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        fontWeight: "800",
                        color: semanticColors.action.tonal.foreground,
                      }}
                    >
                      {t(
                        hasUploadError
                          ? "you.insurance_modal.retry_card_button"
                          : "you.insurance_modal.add_card_button",
                      )}
                    </Text>
                  </View>
                )}
              </Pressable>
            );
          })}
        </CardSurface>
      </MoonFormField>

      {/* Carrier */}
      <MoonFormField label={t("you.insurance_modal.carrier_label")}>
        <TextField
          value={carrier}
          onChangeText={(value) => {
            if (value !== carrier) setPayerId(null);
            setCarrier(value);
            setFormError(false);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          accessibilityLabel={t("you.insurance_modal.carrier_label")}
          autoCapitalize="words"
          autoCorrect={false}
          returnKeyType="next"
          placeholder={t("you.insurance_modal.carrier_placeholder")}
        />
        {showSuggestions &&
          carrier.trim().length > 0 &&
          (payersSearchFailed ? (
            <FieldMessage style={{ marginTop: 6 }}>
              {t("you.insurance_modal.carrier_search_unavailable")}
            </FieldMessage>
          ) : isSearchingPayers && !payerSuggestions ? (
            <View
              style={[
                selectMenuSurfaceStyle,
                selectMenuItemStyle(false),
                { marginTop: 4 },
              ]}
            >
              <ActivityIndicator
                size="small"
                color={semanticColors.text.tertiary}
              />
              <Text
                className="font-dm-sans"
                style={{ fontSize: 13, color: EMBER.colors.textLight }}
              >
                {t("you.insurance_modal.carrier_searching")}
              </Text>
            </View>
          ) : payerSuggestions && payerSuggestions.length > 0 ? (
            <View style={[selectMenuSurfaceStyle, { marginTop: 4 }]}>
              <ScrollView
                bounces={false}
                keyboardShouldPersistTaps="handled"
                style={{ maxHeight: 160 }}
              >
                {payerSuggestions.slice(0, 6).map((payer) => (
                  <Pressable
                    key={payer.id}
                    onPress={() => {
                      capturePress(
                        pressLabel(payer.displayName ?? payer.name),
                        "payer_suggestion",
                      );
                      setCarrier(payer.displayName ?? payer.name);
                      setPayerId(payer.id);
                      setShowSuggestions(false);
                    }}
                    style={({ pressed }) => selectMenuItemStyle(pressed)}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{ fontSize: 14, color: EMBER.colors.text }}
                    >
                      {payer.displayName ?? payer.name}
                    </Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          ) : null)}
      </MoonFormField>

      {/* Member ID */}
      <TextField
        label={t("you.insurance_modal.member_id_label")}
        value={memberId}
        onChangeText={(value) => {
          setMemberId(value);
          setFormError(false);
        }}
        autoCapitalize="characters"
        autoCorrect={false}
        returnKeyType="next"
        placeholder={t("you.insurance_modal.member_id_placeholder")}
      />

      {/* Group number */}
      <TextField
        label={t("you.insurance_modal.group_number_label")}
        value={groupNumber}
        onChangeText={(value) => {
          setGroupNumber(value);
          setFormError(false);
        }}
        autoCapitalize="characters"
        autoCorrect={false}
        returnKeyType="done"
        placeholder={t("you.insurance_modal.group_number_placeholder")}
      />

      {/* Error */}
      {formError && (
        <FieldMessage centered>
          {t("you.insurance_modal.no_details_error")}
        </FieldMessage>
      )}
      {saveCoverage.isError && (
        <ActionBanner
          variant="important"
          message={t("you.insurance_modal.error_generic")}
          actionLabel={t("clinical_data.retry")}
          action="retry_save_insurance"
          actionIcon={RotateCcw}
          onAction={() => void handleSubmit()}
        />
      )}

      <DialogActions>
        <SecondaryButton
          label={t("you.insurance_modal.cancel_button")}
          onPress={onCancel}
          disabled={saveCoverage.isPending}
          flex={1}
        />
        <PrimaryButton
          label={t("you.insurance_modal.save_button")}
          onPress={handleSubmit}
          disabled={saveCoverage.isPending || processingSides.size > 0}
          loading={saveCoverage.isPending || processingSides.size > 0}
          flex={1}
        />
      </DialogActions>

      {/* Edit one side of the card: a new upload, or removal. Removal is
          staged until Save, so Cancel on the form still brings it back. */}
      <Dialog
        visible={manageSide !== null}
        onClose={() => setManageSide(null)}
        layout="centered"
        closeButton="minimal"
        closeAction="close_insurance_card_side"
      >
        <DialogHeader
          title={t(
            manageSide === "back"
              ? "you.insurance_modal.back_of_your_insurance_card"
              : "you.insurance_modal.front_of_your_insurance_card",
          )}
          subtitle={t("you.insurance_modal.upload_new_card_or_remove")}
          style={{ marginBottom: 16 }}
        />
        <DialogActions style={{ marginTop: 0 }}>
          <PrimaryButton
            label={t("you.insurance_modal.upload_new_card_image")}
            action="you.insurance_modal.upload_new_card_image"
            onPress={() => {
              const side = manageSide;
              setManageSide(null);
              if (side) handlePickCard(side);
            }}
          />
        </DialogActions>
        <TextActionButton
          label={t("you.insurance_modal.remove_card_image")}
          action="you.insurance_modal.remove_card_image"
          tone="destructive"
          centered
          onPress={() => {
            if (manageSide) handleDeleteCard(manageSide);
          }}
          style={{ marginTop: 8 }}
        />
      </Dialog>
    </View>
  );
}

function formatCents(cents: number | null): string | null {
  if (cents === null) return null;
  return `$${(cents / 100).toLocaleString()}`;
}

function VerificationStatusSummary({ status }: { status: string }) {
  const t = useTranslations();
  const config = {
    verified: {
      labelKey: "you.insurance_modal.status_verified",
      descKey: "you.insurance_modal.status_verified_description",
      tone: "green",
    },
    pending: {
      labelKey: "you.insurance_modal.status_pending",
      descKey: "you.insurance_modal.status_pending_description",
      tone: "gold",
    },
    unverified: {
      labelKey: "you.insurance_modal.status_unverified",
      descKey: "you.insurance_modal.status_unverified_description",
      tone: "neutral",
    },
  } as const;
  const key = status as keyof typeof config;
  const { labelKey, descKey, tone } =
    key in config ? config[key] : config.unverified;

  return (
    <View style={{ gap: 6, alignItems: "flex-start" }}>
      <StatusBadge
        label={t(labelKey)}
        tone={tone}
        icon={key === "verified" ? CheckCircle2 : undefined}
      />
      <SupportingSubtitleText>{t(descKey)}</SupportingSubtitleText>
    </View>
  );
}

export default function InsuranceInfoModal({
  visible,
  onClose,
  primaryPolicy,
  hasInsurance,
  verificationStatus,
  fertilityCoverage,
  isLoading,
  initialMode = "edit",
}: Props) {
  const t = useTranslations();
  const [isEditing, setIsEditing] = useState(false);
  // Whether the modal opened with no insurance on file. Captured once per
  // open (after loading settles) so a background coverage refetch cannot
  // switch the user out of the add form into the read-only summary mid-flow.
  const [startedWithoutInsurance, setStartedWithoutInsurance] = useState(false);
  const wasVisibleRef = useRef(false);
  useEffect(() => {
    if (visible && !isLoading && !wasVisibleRef.current) {
      wasVisibleRef.current = true;
      setStartedWithoutInsurance(!hasInsurance);
      setIsEditing(hasInsurance && initialMode === "edit");
    } else if (!visible && wasVisibleRef.current) {
      wasVisibleRef.current = false;
      setStartedWithoutInsurance(false);
      setIsEditing(false);
    }
  }, [visible, isLoading, hasInsurance, initialMode]);

  const providerName =
    primaryPolicy?.payer?.displayName ??
    primaryPolicy?.payer?.name ??
    primaryPolicy?.insuranceCarrier ??
    null;

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      contentStyle={{ paddingBottom: 16 }}
    >
      <DialogIcon source={umbrellaIcon} width={92} height={80} />
      <DialogTitleText
        style={{ textAlign: "center", marginTop: 16, marginBottom: 12 }}
      >
        {t("you.insurance_modal.title")}
      </DialogTitleText>

      <View style={{ flexShrink: 1 }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          style={{ maxHeight: 460 }}
          contentContainerStyle={{ paddingBottom: 24 }}
        >
          {isLoading ? (
            <LoadingState style={{ paddingVertical: 40 }} />
          ) : startedWithoutInsurance || !hasInsurance ? (
            <InsuranceForm
              key="new-insurance"
              onSuccess={onClose}
              onCancel={onClose}
            />
          ) : isEditing ? (
            <InsuranceForm
              key={primaryPolicy?.id ?? "insurance-policy"}
              isEdit
              initialValues={{
                carrier:
                  primaryPolicy?.payer?.displayName ??
                  primaryPolicy?.payer?.name ??
                  primaryPolicy?.insuranceCarrier ??
                  "",
                payerId: primaryPolicy?.payerId ?? primaryPolicy?.payer?.id,
                memberId: primaryPolicy?.memberId ?? "",
                groupNumber: primaryPolicy?.groupNumber ?? "",
                policyId: primaryPolicy?.id,
              }}
              onSuccess={() => setIsEditing(false)}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <>
              {/* Policy info, with its verification status on top */}
              <CardSurface style={{ padding: 16, gap: 14 }}>
                <VerificationStatusSummary status={verificationStatus} />
                <MoonDetailRow
                  icon={Shield}
                  label={t("you.insurance_modal.provider_label")}
                  value={providerName ?? t("you.insurance_modal.not_provided")}
                />
                <MoonDetailRow
                  icon={Building2}
                  label={t("you.insurance_modal.plan_name_label")}
                  value={
                    primaryPolicy?.planName ??
                    t("you.insurance_modal.not_provided")
                  }
                />
                <MoonDetailRow
                  icon={Hash}
                  label={t("you.insurance_modal.member_id_label")}
                  value={
                    primaryPolicy?.memberId ??
                    t("you.insurance_modal.not_provided")
                  }
                />
                {primaryPolicy?.groupNumber && (
                  <MoonDetailRow
                    icon={Hash}
                    label={t("you.insurance_modal.group_number_summary_label")}
                    value={primaryPolicy.groupNumber}
                  />
                )}
                {primaryPolicy?.planType && (
                  <MoonDetailRow
                    icon={Shield}
                    label={t("you.insurance_modal.plan_type_label")}
                    value={primaryPolicy.planType}
                  />
                )}
              </CardSurface>

              {/* Fertility coverage summary */}
              {fertilityCoverage && fertilityCoverage.hasCoverage && (
                <View style={{ marginTop: 16, gap: 10 }}>
                  <SectionTitle>
                    {t("you.insurance_modal.fertility_coverage_header")}
                  </SectionTitle>
                  <CardSurface style={{ padding: 16, gap: 14 }}>
                    {fertilityCoverage.deductibleCents !== null && (
                      <MoonDetailRow
                        icon={Heart}
                        label={t("you.insurance_modal.deductible_label")}
                        value={
                          formatCents(fertilityCoverage.deductibleCents) ??
                          t("you.insurance_modal.not_provided")
                        }
                      />
                    )}
                    {fertilityCoverage.outOfPocketMaxCents !== null && (
                      <MoonDetailRow
                        icon={Heart}
                        label={t("you.insurance_modal.oop_max_label")}
                        value={
                          formatCents(fertilityCoverage.outOfPocketMaxCents) ??
                          t("you.insurance_modal.not_provided")
                        }
                      />
                    )}
                    {fertilityCoverage.coinsurancePercent !== null && (
                      <MoonDetailRow
                        icon={Heart}
                        label={t("you.insurance_modal.coinsurance_label")}
                        value={`${fertilityCoverage.coinsurancePercent}%`}
                      />
                    )}
                    {(fertilityCoverage.treatmentsCovered?.length ?? 0) > 0 && (
                      <MoonDetailRow
                        icon={Heart}
                        label={t(
                          "you.insurance_modal.treatments_covered_label",
                        )}
                        value={fertilityCoverage.treatmentsCovered?.join(", ")}
                      />
                    )}
                  </CardSurface>
                </View>
              )}
            </>
          )}
        </ScrollView>
      </View>
      {!isLoading && hasInsurance && !startedWithoutInsurance && !isEditing ? (
        <DialogActions>
          <PrimaryButton
            label={t("you.insurance_card.edit")}
            action="you.insurance_modal.edit"
            onPress={() => setIsEditing(true)}
          />
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
